import { faFolderPlus, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { TemplatePicker, WhenBox } from './ScheduleFields'
import { TemplateCard } from './TemplateCard'
import { TemplateEditor } from './TemplateEditor'
import { TemplateList } from './TemplateList'
import { TemplateFormState, formFrom } from './templateFormState'
import {
    Deployment,
    Options,
    Template,
    TemplateGroup,
    Window,
    deleteGroup,
    deleteTemplate,
    deleteWindow,
    deployTemplate,
    getDeployments,
    getOptions,
    getTemplates,
    reorderTemplates,
    saveGroup,
    saveTemplate,
    saveWindow,
} from './templatesApi'

/**
 * Schedules are stored in UTC and entered in whatever zone the operator is in.
 *
 * `datetime-local` carries no zone, and this used to bridge that by appending
 * "Z" to whatever was typed -- which declared the operator's wall clock to be
 * UTC. Someone at UTC+5 picking 20:00 for a Friday evening got 20:00 UTC, one
 * in the morning their time. Loading a window for editing made the same
 * mistake in reverse, so a round trip looked consistent while firing at the
 * wrong hour, which is the worst version of this bug: nothing on screen
 * disagrees with anything else.
 *
 * The zone is taken from the browser rather than asked for. It is the one
 * piece of information nobody can get wrong.
 */
const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

/** A `datetime-local` value, read as local time, as a UTC ISO string. */
export const localInputToUtc = (value: string): string | null => (value ? new Date(value).toISOString() : null)

/** A UTC ISO string as a `datetime-local` value in the browser's zone. */
export const utcToLocalInput = (iso: string): string => {
    if (!iso) return ''
    const d = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** A UTC ISO string as readable local time, e.g. "Fri 12 Aug, 20:00". */
export const showLocal = (iso: string): string =>
    iso
        ? new Date(iso).toLocaleString(undefined, {
              weekday: 'short',
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
          })
        : ''

// Poll only while something is building. A fixed interval would keep hitting
// GitHub's API all day to learn nothing.
const POLL_MS = 10000

export const runLabel = (d: Deployment) => {
    if (d.run_conclusion === 'failure') return { text: 'failed', style: 'text-red-400' }
    if (d.run_conclusion === 'success') return { text: 'built, registering…', style: 'text-green-400' }
    if (d.run_status === 'in_progress') return { text: 'building…', style: 'text-blue-400' }
    if (d.run_status === 'queued') return { text: 'queued', style: 'text-white/60' }
    return { text: 'dispatched', style: 'text-white/60' }
}

export const Templates = ({ canEdit }: { canEdit: boolean }) => {
    const { addNotification } = useNotification()
    const [templates, setTemplates] = useState<Template[]>([])
    const [groups, setGroups] = useState<TemplateGroup[]>([])
    const [windows, setWindows] = useState<Window[]>([])
    const [options, setOptions] = useState<Options | null>(null)
    const [deployments, setDeployments] = useState<Deployment[]>([])
    const [creating, setCreating] = useState(false)
    const [expanded, setExpanded] = useState<number | null>(null)
    const [counts, setCounts] = useState<Record<number, number>>({})
    // Which region each template's next deployment targets. Only meaningful for
    // templates carrying more than one; the card hides the picker otherwise.
    const [regions, setRegions] = useState<Record<number, string>>({})
    // Which GCP family each template's next deploy asks for.
    const [machineTypes, setMachineTypes] = useState<Record<number, string>>({})
    // Pre-filled state when duplicating, else null for a blank new template.
    const [seed, setSeed] = useState<TemplateFormState | null>(null)
    // Which row is asking to be confirmed. A native confirm() blocks the tab
    // and cannot name what it is about to delete.
    const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null)
    const [confirmingGroup, setConfirmingGroup] = useState<TemplateGroup | null>(null)
    // Non-null while the "new group" row is open. A name is required, and
    // window.prompt is not an option on these cards -- see ConfirmBar.
    const [newGroup, setNewGroup] = useState<string | null>(null)

    const refresh = useCallback(async () => {
        try {
            const [data, opts] = await Promise.all([getTemplates(), getOptions()])
            setTemplates(data.templates)
            setGroups(data.groups ?? [])
            setWindows(data.windows)
            setOptions(opts)
        } catch (err) {
            addNotification(err.message, 'error')
        }
    }, [])

    const pollDeployments = useCallback(async () => {
        try {
            const { deployments } = await getDeployments()
            setDeployments(deployments)
            return deployments
        } catch {
            return []
        }
    }, [])

    useEffect(() => {
        refresh()
        pollDeployments()
    }, [refresh, pollDeployments])

    useEffect(() => {
        if (!deployments.length) return
        const timer = setInterval(async () => {
            const current = await pollDeployments()
            if (!current.length) refresh()
        }, POLL_MS)
        return () => clearInterval(timer)
    }, [deployments.length, pollDeployments, refresh])

    // The card batches its edits, so this takes whatever changed. Pools and
    // tournaments arrive as whole collections because saveTemplate replaces
    // them -- sending a partial list would delete the rest.
    const saveDraft = (template: Template) => async (changes: Record<string, any>) => {
        try {
            await saveTemplate(template.id, changes)
            addNotification(`Saved ${Object.keys(changes).join(', ')}`, 'success')
        } catch (err) {
            addNotification(err.message, 'error')
            throw err
        } finally {
            // Reload either way, so the card shows what is actually stored
            // rather than a rejected value that still looks saved.
            await refresh()
        }
    }

    const deploy = (template: Template) => async () => {
        const count = counts[template.id] ?? 1
        // Defaults to the template's first region, which is the only one for
        // every template that predates templates carrying a set.
        const region = regions[template.id] ?? template.deploy_slot_regions?.[0]
        const machineType = machineTypes[template.id]
        try {
            const result = await deployTemplate(template.id, count, region, machineType)
            addNotification(
                `Deploying ${result.started.join(', ') || 'nothing'}`,
                result.started.length ? 'success' : 'error'
            )
            result.failed.forEach((f: any) => addNotification(`${f.slot}: ${f.error}`, 'error'))
        } catch (err) {
            addNotification(err.message, 'error')
        } finally {
            pollDeployments()
        }
    }

    /**
     * Store a rearranged screen, showing it first.
     *
     * Optimistic because a drag that snaps back for a round trip reads as a
     * failed drag, and the operator does it again. On failure the list is
     * reloaded rather than rolled back locally, so what is on screen is what
     * is actually stored -- a half-applied arrangement nobody can see is worse
     * than a visible jump.
     */
    const arrange = async (nextTemplates: Template[], nextGroups: TemplateGroup[]) => {
        setTemplates(nextTemplates)
        setGroups(nextGroups)
        try {
            await reorderTemplates(
                nextTemplates.map((t) => ({ id: t.id, group_id: t.group_id ?? null })),
                nextGroups.map((g) => ({ id: g.id }))
            )
        } catch (err) {
            addNotification(err.message, 'error')
            refresh()
        }
    }

    const createGroup = async (name: string) => {
        setNewGroup(null)
        try {
            await saveGroup(null, { name })
            addNotification(`Group "${name}" created`, 'success')
        } catch (err) {
            addNotification(err.message, 'error')
        } finally {
            refresh()
        }
    }

    const renameGroup = async (group: TemplateGroup, name: string) => {
        // Shown before it is stored, for the same reason as arrange(): a name
        // that reverts for a round trip looks like the rename was rejected.
        setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, name } : g)))
        try {
            await saveGroup(group.id, { name })
        } catch (err) {
            addNotification(err.message, 'error')
            refresh()
        }
    }

    /** Collapse is a shared default, so it is stored -- but never waited on. */
    const collapseGroup = async (group: TemplateGroup, collapsed: boolean) => {
        setGroups((prev) => prev.map((g) => (g.id === group.id ? { ...g, collapsed } : g)))
        try {
            await saveGroup(group.id, { collapsed })
        } catch (err) {
            addNotification(err.message, 'error')
        }
    }

    const removeGroup = (group: TemplateGroup) => async () => {
        setConfirmingGroup(null)
        try {
            const result = await deleteGroup(group.id)
            addNotification(
                `Group deleted; ${result.released} template${result.released === 1 ? '' : 's'} ungrouped`,
                'success'
            )
        } catch (err) {
            addNotification(err.message, 'error')
        } finally {
            refresh()
        }
    }

    const remove = (template: Template) => async () => {
        setConfirmingDelete(null)
        try {
            await deleteTemplate(template.id)
            addNotification('Template deleted', 'success')
        } catch (err) {
            addNotification(err.message, 'error')
        } finally {
            refresh()
        }
    }

    if (!options) return <div className='text-xs opacity-40'>loading…</div>

    return (
        <div className='flex flex-col gap-8 text-sm'>
            {deployments.length > 0 && (
                <section className='flex flex-col gap-2'>
                    <header className='text-xs uppercase tracking-widest text-white/50'>
                        Building now ({deployments.length})
                    </header>
                    {deployments.map((d) => {
                        const label = runLabel(d)
                        return (
                            <div
                                key={d.slot}
                                className='flex flex-row items-center gap-3 border border-blue-500/30 bg-blue-500/5 rounded px-3 py-2 text-xs'
                            >
                                <span className='font-mono shrink-0'>{d.slot}</span>
                                <span className={`shrink-0 ${label.style}`}>{label.text}</span>
                                <span className='opacity-50 grow'>
                                    {d.template} · {d.requested_by ?? 'a schedule'} · {Math.round(d.age_seconds / 60)}m
                                    ago
                                </span>
                                {d.run_url && (
                                    <a
                                        href={d.run_url}
                                        target='_blank'
                                        rel='noreferrer'
                                        className='underline opacity-70'
                                    >
                                        logs
                                    </a>
                                )}
                            </div>
                        )
                    })}
                </section>
            )}

            <section className='flex flex-col gap-2'>
                <header className='flex flex-row items-center justify-between'>
                    <span className='text-xs uppercase tracking-widest text-white/50'>
                        Templates ({templates.length})
                    </span>
                    <div className='flex flex-row items-center gap-2'>
                        <ActionButton
                            onClick={async () => setNewGroup('')}
                            disabled={!canEdit}
                            title={canEdit ? 'Create a group to file templates under' : 'Requires the executive role'}
                        >
                            <FontAwesomeIcon icon={faFolderPlus} className='mr-1' /> New group
                        </ActionButton>
                        <ActionButton
                            onClick={async () => {
                                setSeed(null)
                                setCreating(true)
                            }}
                            disabled={!canEdit}
                            title={canEdit ? 'Create a template' : 'Requires the executive role'}
                            className='text-amber-400'
                        >
                            <FontAwesomeIcon icon={faPlus} className='mr-1' /> New template
                        </ActionButton>
                    </div>
                </header>

                {newGroup !== null && (
                    <div className='flex flex-row items-center gap-2 rounded border border-white/20 p-2'>
                        <span className='text-[10px] uppercase tracking-widest text-white/50'>group name</span>
                        <input
                            autoFocus
                            value={newGroup}
                            onChange={(e) => setNewGroup(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && newGroup.trim()) createGroup(newGroup.trim())
                                if (e.key === 'Escape') setNewGroup(null)
                            }}
                            placeholder='Hangar'
                            className='input-team-tools py-1 px-2 text-xs grow max-w-xs'
                        />
                        <ActionButton
                            onClick={async () => newGroup.trim() && createGroup(newGroup.trim())}
                            disabled={!newGroup.trim()}
                            className='text-amber-400'
                        >
                            Create
                        </ActionButton>
                        <ActionButton onClick={async () => setNewGroup(null)}>Cancel</ActionButton>
                    </div>
                )}

                {creating && (
                    <TemplateEditor
                        // Remount when the seed changes: useState only reads
                        // its initial value once, so duplicating a second
                        // template would otherwise still show the first.
                        key={seed?.name ?? 'new'}
                        seed={seed}
                        options={options}
                        canEdit={canEdit}
                        onSaved={() => {
                            setCreating(false)
                            setSeed(null)
                            refresh()
                        }}
                        onCancel={() => {
                            setCreating(false)
                            setSeed(null)
                        }}
                    />
                )}

                {confirmingGroup && (
                    <ConfirmBar
                        message={`Delete the group "${confirmingGroup.name}"?`}
                        detail='The templates in it are not deleted; they move back to the ungrouped list.'
                        confirmLabel='Delete group'
                        onCancel={() => setConfirmingGroup(null)}
                        onConfirm={removeGroup(confirmingGroup)}
                    />
                )}

                <TemplateList
                    templates={templates}
                    groups={groups}
                    canEdit={canEdit}
                    onArrange={arrange}
                    onCollapse={collapseGroup}
                    onRenameGroup={renameGroup}
                    onDeleteGroup={setConfirmingGroup}
                    renderTemplate={(t, dragHandle, dragging) => (
                        <TemplateCard
                            template={t}
                            options={options}
                            canEdit={canEdit}
                            dragHandle={dragHandle}
                            dragging={dragging}
                            expanded={expanded === t.id}
                            onToggle={() => setExpanded(expanded === t.id ? null : t.id)}
                            onSave={saveDraft(t)}
                            count={counts[t.id] ?? 1}
                            onCountChange={(n: number) => setCounts((prev) => ({ ...prev, [t.id]: n }))}
                            region={regions[t.id]}
                            onRegionChange={(r: string) => setRegions((prev) => ({ ...prev, [t.id]: r }))}
                            machineType={machineTypes[t.id]}
                            onMachineTypeChange={(m: string) => setMachineTypes((prev) => ({ ...prev, [t.id]: m }))}
                            onDuplicate={() => {
                                // Name has to change: it is unique, so an
                                // unedited copy would be refused on save.
                                setSeed({ ...formFrom(t), name: `${t.name} copy` })
                                setCreating(true)
                            }}
                            onDeploy={deploy(t)}
                            onDelete={async () => setConfirmingDelete(t.id)}
                            confirmSlot={
                                confirmingDelete === t.id && (
                                    <ConfirmBar
                                        message={`Delete the template "${t.name}"?`}
                                        detail='Servers already deployed from it keep running and keep their settings.'
                                        confirmLabel='Delete template'
                                        onCancel={() => setConfirmingDelete(null)}
                                        onConfirm={remove(t)}
                                    />
                                )
                            }
                        />
                    )}
                />

                {!templates.length && !creating && (
                    <div className='py-6 text-center opacity-40 text-xs'>No templates yet.</div>
                )}

                <span className='text-[11px] opacity-40'>
                    Deploy dispatches the build; the server applies the whole template to itself when it comes up:
                    naming, fee, capacity, map, requirements, loot and tournaments.
                    {canEdit &&
                        ' Drag a card by its handle to reorder it or move it between groups; the arrangement is shared and saved as you drop.'}
                </span>
            </section>

            <Schedules windows={windows} templates={templates} canEdit={canEdit} onChange={refresh} />
        </div>
    )
}

export const Schedules = ({ windows, templates, canEdit, onChange }) => {
    const { addNotification } = useNotification()
    const [draft, setDraft] = useState<any>(null)
    const [confirmingWindow, setConfirmingWindow] = useState<number | null>(null)

    const blank = () => ({
        name: '',
        // Enabled by default: filling in a schedule is the act of wanting it to
        // run, and a form that quietly saves an inert one is how "I scheduled
        // it and it never ran" happens. Untick it deliberately to park one.
        enabled: true,
        // A schedule is a list of "N of this template", because an event
        // wanting two Duos and a Solos used to need two schedules with the
        // same times -- easy to change one and forget the other.
        templates: [{ profile_id: templates[0]?.id, count: 1 }],
        deploy_at: '',
        destroy_at: '',
        repeat_weekly: true,
    })

    const save = async () => {
        try {
            await saveWindow(draft.id ?? null, {
                ...draft,
                // Sent whole rather than as a patch: the editor holds the entire
                // list, so a partial update is indistinguishable from a removal.
                templates: draft.templates.filter((t: any) => t.profile_id),
                // Typed in the operator's zone, stored in UTC.
                deploy_at: localInputToUtc(draft.deploy_at),
                destroy_at: localInputToUtc(draft.destroy_at),
            })
            addNotification('Schedule saved', 'success')
            setDraft(null)
        } catch (err) {
            addNotification(err.message, 'error')
        } finally {
            onChange()
        }
    }

    const remove = (w: Window) => async () => {
        setConfirmingWindow(null)
        try {
            await deleteWindow(w.id)
            addNotification('Schedule deleted', 'success')
        } catch (err) {
            addNotification(err.message, 'error')
        } finally {
            onChange()
        }
    }

    const field = 'bg-transparent border border-white/30 rounded py-1 px-2 outline-none text-xs'

    return (
        <section className='flex flex-col gap-3'>
            <header className='flex flex-row items-center justify-between'>
                <span className='text-xs uppercase tracking-widest text-white/50'>
                    Scheduled capacity ({windows.length})
                </span>
                <ActionButton
                    onClick={async () => setDraft(blank())}
                    disabled={!canEdit || !templates.length}
                    title={templates.length ? '' : 'Create a template first'}
                    className='text-amber-400'
                >
                    <FontAwesomeIcon icon={faPlus} className='mr-1' /> New schedule
                </ActionButton>
            </header>

            {draft && (
                <div className='border border-white/20 rounded p-4 flex flex-col gap-5'>
                    <label className='flex flex-col gap-1 max-w-xs'>
                        <span className='text-[10px] uppercase tracking-widest text-white/50'>name</span>
                        <input
                            className={field}
                            value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                    </label>

                    <TemplatePicker
                        rows={draft.templates}
                        templates={templates}
                        onChange={(rows) => setDraft({ ...draft, templates: rows })}
                    />

                    <WhenBox
                        deployAt={draft.deploy_at}
                        destroyAt={draft.destroy_at}
                        onChange={(next) => setDraft({ ...draft, ...next })}
                    />

                    <div className='flex flex-row flex-wrap gap-6'>
                        <label className='flex flex-row items-center gap-2 text-xs cursor-pointer'>
                            <input
                                type='checkbox'
                                checked={draft.repeat_weekly}
                                onChange={(e) => setDraft({ ...draft, repeat_weekly: e.target.checked })}
                                className='accent-violet-500'
                            />
                            repeat weekly
                        </label>
                        <label className='flex flex-row items-center gap-2 text-xs cursor-pointer'>
                            <input
                                type='checkbox'
                                checked={draft.enabled}
                                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                                className='accent-violet-500'
                            />
                            enabled
                        </label>
                    </div>
                    <div className='flex flex-row gap-3 items-center'>
                        <ActionButton onClick={save} className='text-amber-400'>
                            Save schedule
                        </ActionButton>
                        <ActionButton onClick={async () => setDraft(null)}>Cancel</ActionButton>
                        <span className='text-[11px] opacity-40'>
                            Leaving "destroy at" empty keeps the servers up, which is how capacity gets forgotten about,
                            so set it unless you mean it.
                        </span>
                    </div>
                </div>
            )}

            <div className='text-[11px] text-white/40 pb-2'>
                Times are shown and entered in your timezone ({BROWSER_TIMEZONE}); they are stored in UTC.
            </div>

            <div className='overflow-x-auto'>
                <table className='w-full text-xs border-collapse'>
                    <thead className='text-white/50 uppercase tracking-widest text-[10px]'>
                        <tr className='border-b border-white/10'>
                            {['name', 'template', 'count', 'deploys', 'destroys', 'repeats', 'last run', ''].map(
                                (h) => (
                                    <th key={h} className='text-left font-normal uppercase tracking-wide py-2 pr-3'>
                                        {h}
                                    </th>
                                )
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {windows.map((w: Window) => (
                            <Fragment key={w.id}>
                                <tr className='border-b border-white/5 hover:bg-white/5'>
                                    <td className='py-2 pr-3'>
                                        <span className={w.enabled ? '' : 'opacity-40'}>{w.name}</span>
                                        {/* Loud on purpose. A disabled schedule looks identical to a
                                        working one in every other column -- it has a template, a
                                        time and a repeat -- and the only clue used to be dimmed
                                        text. "Scheduled it and it never ran" starts here. */}
                                        {!w.enabled && (
                                            <span className='ml-2 rounded border border-red-500/50 bg-red-500/10 px-[6px] py-[1px] text-[10px] uppercase tracking-wider text-red-400'>
                                                disabled, will not run
                                            </span>
                                        )}
                                    </td>
                                    <td className='py-2 pr-3'>{w.summary ?? w.profile}</td>
                                    <td className='py-2 pr-3'>{w.total_servers ?? w.count}</td>
                                    {/* Shown in the operator's zone, the same one the form accepts.
                                    A list in UTC beside a form in local time is how a schedule
                                    gets read back as correct while firing hours out. */}
                                    <td className='py-2 pr-3'>{showLocal(w.deploy_at)}</td>
                                    <td className='py-2 pr-3'>
                                        {w.destroy_at ? (
                                            showLocal(w.destroy_at)
                                        ) : (
                                            <span className='text-amber-400'>never</span>
                                        )}
                                    </td>
                                    <td className='py-2 pr-3'>{w.repeat_weekly ? 'weekly' : 'once'}</td>
                                    <td className='py-2 pr-3 opacity-60'>
                                        {w.last_deployed_at ? showLocal(w.last_deployed_at) : 'never'}
                                    </td>
                                    <td className='py-2'>
                                        <div className='flex flex-row gap-2 justify-end'>
                                            <ActionButton
                                                onClick={async () =>
                                                    setDraft({
                                                        ...w,
                                                        // Older schedules predate the through model, so
                                                        // fall back to the single template they carry.
                                                        templates: w.templates?.length
                                                            ? w.templates.map((t) => ({
                                                                  profile_id: t.profile_id,
                                                                  count: t.count,
                                                              }))
                                                            : [{ profile_id: w.profile_id, count: w.count }],
                                                        deploy_at: utcToLocalInput(w.deploy_at),
                                                        destroy_at: utcToLocalInput(w.destroy_at),
                                                    })
                                                }
                                                disabled={!canEdit}
                                            >
                                                <FontAwesomeIcon icon={faPen} />
                                            </ActionButton>
                                            <ActionButton
                                                onClick={async () => setConfirmingWindow(w.id)}
                                                disabled={!canEdit}
                                                className='text-red-400'
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </ActionButton>
                                        </div>
                                    </td>
                                </tr>
                                {confirmingWindow === w.id && (
                                    <tr key={`${w.id}-confirm`}>
                                        <td colSpan={8} className='py-2'>
                                            <ConfirmBar
                                                message={`Delete the schedule "${w.name}"?`}
                                                detail='Servers it has already deployed stay up; only the schedule goes.'
                                                confirmLabel='Delete schedule'
                                                onCancel={() => setConfirmingWindow(null)}
                                                onConfirm={remove(w)}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                        {!windows.length && (
                            <tr>
                                <td colSpan={8} className='py-6 text-center opacity-40'>
                                    Nothing scheduled.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    )
}
